/**
 * Reemplazo del encode de video WebCodecs por el path NVENC-OSR (directiva: sin doble path).
 *
 * El video ya viene encodeado a H.264 por el addon NVENC (main process) sobre la shared
 * texture del OSR → escrito a un ring buffer SHM. Este sender corre en el OSR window: lanza
 * un worker que `require` el addon, abre `VideoPacketReader.open(ringName)`, y reenvía cada
 * packet al uni-stream de WebTransport con EXACTAMENTE el mismo framing que
 * `video-encoder.worker.ts`: `[type u8][pts BigInt64 BE][size u32 BE][NAL...]`.
 *
 * Drop-in del antiguo `VideoHardwareEncoder` para el path de broadcast (no usa MediaStreamTrack:
 * la captura la hizo NVENC sobre el OSR). El worker necesita `nodeIntegrationInWorker: true`
 * en el OSR window (mismo patrón que BrowserCaptureService).
 */
export class NvencRingVideoSender {
  private worker: Worker | null = null;

  private videoStream: WritableStream<Uint8Array> | null = null;

  private onStopCallback: (() => void) | null = null;

  setOnStop(callback: () => void) {
    this.onStopCallback = callback;
  }

  async init(
    transport: WebTransport,
    ringName: string,
    ringCapacity: number,
    nativePath: string,
  ): Promise<void> {
    this.videoStream = await transport.createUnidirectionalStream();

    // JSON.stringify produce un literal JS válido (escapa backslashes Y comillas del path).
    const nativeLiteral = JSON.stringify(nativePath);

    // Worker (blob) con acceso a Node (require/Buffer). Lee el ring y escribe al uni-stream
    // transferido. Framing idéntico a video-encoder.worker.ts (VIDEO_KEY=1, VIDEO_DELTA=2).
    const workerCode = `
      const native = require(${nativeLiteral});
      const VIDEO_KEY = 1, VIDEO_DELTA = 2;
      let reader = null, writer = null, stopped = false, firstTs = null, count = 0;

      self.onmessage = async (e) => {
        const msg = e.data;
        if (msg && msg.type === 'init') {
          try {
            reader = native.VideoPacketReader.open(msg.ringName, msg.ringCapacity);
            writer = msg.stream.getWriter();
          } catch (err) {
            self.postMessage({ type: 'error', message: 'open: ' + String(err) });
            return;
          }
          const buf = Buffer.allocUnsafe(8 * 1024 * 1024); // 8MB reusable
          const loop = async () => {
            while (!stopped) {
              try {
                if (!reader.waitForPacket(100)) continue;
                let meta = reader.readPacketInto(buf);
                while (meta && !stopped) {
                  if (firstTs === null) firstTs = meta.timestamp;
                  const size = meta.size;
                  const out = new ArrayBuffer(13 + size);
                  const view = new DataView(out);
                  view.setUint8(0, meta.isKeyframe ? VIDEO_KEY : VIDEO_DELTA);
                  view.setBigInt64(1, BigInt(meta.timestamp - firstTs), false);
                  view.setUint32(9, size, false);
                  new Uint8Array(out, 13).set(buf.subarray(0, size));
                  await writer.write(new Uint8Array(out));
                  count++;
                  meta = reader.readPacketInto(buf);
                }
              } catch (err) {
                self.postMessage({ type: 'error', message: String(err) });
                await new Promise((r) => setTimeout(r, 16));
              }
            }
          };
          loop();
          self.postMessage({ type: 'started' });
        } else if (msg === 'stop' || (msg && msg.type === 'stop')) {
          stopped = true;
          try { if (writer) await writer.close(); } catch (_) {}
          self.close();
        }
      };
    `;

    const blob = new Blob([workerCode], { type: "application/javascript" });
    const blobUrl = URL.createObjectURL(blob);
    this.worker = new Worker(blobUrl);
    URL.revokeObjectURL(blobUrl);

    this.worker.onmessage = (e) => {
      const msg = e.data;
      if (msg?.type === "error") {
        console.error("[NvencRingVideoSender] worker error:", msg.message);
        if (this.onStopCallback) this.onStopCallback();
      } else if (msg?.type === "started") {
        console.log(
          "[NvencRingVideoSender] worker started — leyendo ring NVENC y enviando al uni-stream",
        );
      }
    };

    // Un fallo top-level del worker (p.ej. `require` indefinido si la ventana no tiene Node en
    // workers) NO llega por onmessage → sin esto quedaba invisible. Lo surfaceamos.
    this.worker.onerror = (ev) => {
      console.error(
        "[NvencRingVideoSender] worker onerror:",
        (ev as ErrorEvent)?.message || String(ev),
      );
      if (this.onStopCallback) this.onStopCallback();
    };

    // Transferir el WritableStream del uni-stream al worker.
    this.worker.postMessage(
      { type: "init", ringName, ringCapacity, stream: this.videoStream },
      [this.videoStream as unknown as Transferable],
    );
  }

  // Sin MediaStreamTrack: NVENC ya capturó el OSR. El worker ya está en loop tras init().
  async start(): Promise<void> {
    /* no-op: el loop arranca en init() */
  }

  async stop(): Promise<void> {
    if (this.worker) {
      this.worker.postMessage("stop");
      this.worker = null;
    }
    this.videoStream = null;
  }
}
